import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useLocalization } from "../../hooks/useLocalization";
import { documentApi } from "../../services/documentApi";
import { DocumentViewer } from "../../components/DocumentViewer";
import { FileUploader } from "../../components/FileUploader";
import { EagleState } from "../../components/EagleState";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { DOCUMENT_CATEGORIES } from "../../lib/constants";

export default function MyDocumentsPage() {
  const { user } = useAuth();
  const { t } = useLocalization();
  const [documents, setDocuments] = useState([]);
  const [category, setCategory] = useState("IDENTIFICATION");
  const [accessDenied, setAccessDenied] = useState(false);

  const load = useCallback(() => {
    if (user?.employee_id) {
      documentApi.listByEmployee(user.employee_id)
        .then((r) => { setDocuments(r.data); setAccessDenied(false); })
        .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); });
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file) => {
    if (!user?.employee_id) return;
    await documentApi.upload(user.employee_id, category, file);
    load();
  };

  const handleDownload = async (doc) => {
    const { data } = await documentApi.download(doc.id);
    const url = URL.createObjectURL(data);
    window.open(url, "_blank");
  };

  return (
    <div data-testid="my-documents-page" className="space-y-5">
      {accessDenied ? (
        <AccessDenied resourceLabel={t("nav.myDocuments")} />
      ) : (
      <>
      <PageHelpBanner pageKey="my-documents" title="How to use this page">
        <p>Pick the correct category, then upload a clear photo or scan of the document - HR will review and approve it. You can re-upload anytime if a document is rejected or needs to be renewed.</p>
      </PageHelpBanner>
      <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("nav.myDocuments")}</h2>
      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
        <select data-testid="document-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm">
          {DOCUMENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <FileUploader onUpload={handleUpload} />
      </div>
      {documents.length === 0 ? (
        <EagleState variant="empty" title={t("eagle.noPendingDocsTitle")} message={t("eagle.noPendingDocsMessage")} testId="my-documents-empty" />
      ) : (
        <DocumentViewer documents={documents} onDownload={handleDownload} />
      )}
      </>
      )}
    </div>
  );
}
